import { Component, type ReactNode } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { reportClientError } from "../lib/reportError.js";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : "An unexpected error occurred.";
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, info: { componentStack?: string }) {
    const message = error instanceof Error ? error.message : String(error);
    console.error("[ErrorBoundary]", message, info.componentStack);
    reportClientError(message, info.componentStack ?? undefined);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: "" });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {this.props.fallbackTitle ?? "Something went wrong"}
            </Typography>
            {this.state.message}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={this.handleReset}
          >
            Try again
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}
